<template>
  <div class="items-list-page">
    <div class="items-list-tools">
      <form class="items-searchbar" v-on:submit.prevent>
        <span class="icon icon-magnifying-glass"></span>
        <input
          v-model="search"
          class="items-search-input"
          type="text"
          data-action="grow"
          :placeholder="'Search ' + typeLabel + ' by model, ASIN, or spec'"
        >
      </form>
      <div class="items-count" v-if="allItemsLoaded">
        {{ filteredItems.length }} {{ filteredItems.length === 1 ? 'item' : 'items' }}
      </div>
      <label class="archive-toggle">
        <input
          v-model="showArchived"
          type="checkbox"
          @change="updateArchivedRoute"
        >
        <span>Show archived</span>
      </label>
      <label v-if="allItemsLoaded && filteredItems.length" class="archive-toggle bulk-select-toggle">
        <input
          type="checkbox"
          :checked="allVisibleSelected"
          :indeterminate.prop="someVisibleSelected"
          :disabled="bulkMaintenanceBusy"
          @change="toggleVisibleSelection"
        >
        <span>{{ allVisibleSelected ? 'Clear visible' : 'Select visible' }}</span>
      </label>
    </div>

    <BulkPartMaintenancePanel
      v-if="selectedParts.length"
      :parts="selectedParts"
      @clear="clearSelection"
      @completed="handleBulkCompleted"
      @busy-change="bulkMaintenanceBusy = $event"
    />

    <AdminTable
      v-if="allItemsLoaded && filteredItems.length > 0"
      class="items-admin-table"
      :columns="itemColumns"
      :rows="filteredItems"
      :row-key="itemRowKey"
      clickable
      :row-class="itemRowClass"
      @row-click="popup"
    >
      <template v-slot:cell-select="{ row }">
        <label class="part-select" :aria-label="'Select ' + itemName(row)" @click.stop>
          <input
            type="checkbox"
            :checked="isSelected(row)"
            :disabled="bulkMaintenanceBusy"
            @change="togglePartSelection(row)"
          >
        </label>
      </template>

      <template v-slot:cell-image="{ row }">
        <div class="part-thumb" :class="{ 'is-empty': !mainImage(row) }">
          <img v-if="mainImage(row)" :src="mainImage(row)" :alt="itemName(row)">
          <span v-else class="icon icon-box"></span>
        </div>
      </template>

      <template v-slot:cell-part="{ row }">
        <div class="part-title-line">
          <button type="button" class="part-title" @click.stop="popup(row)">
            {{ itemName(row) }}
          </button>
          <span v-if="isArchived(row)" class="archive-badge">Archived</span>
          <span v-else-if="isUnavailable(row)" class="archive-badge is-unavailable">{{ availabilityLabel(row) }}</span>
        </div>
        <span class="part-subtitle">{{ itemDescription(row) }}</span>
      </template>

      <template v-slot:cell-asin="{ row }">
        <span class="part-id">#{{ row.id || '—' }}</span>
        <span class="part-asin">{{ row.asin || 'No ASIN' }}</span>
      </template>

      <template v-slot:cell-specs="{ row }">
        <div class="spec-chip-list">
          <span
            v-for="chip in partSpecChips(row)"
            :key="chip.label"
            class="spec-chip"
          >
            <span>{{ chip.label }}</span>
            <strong>{{ chip.value }}</strong>
          </span>
          <span v-if="!partSpecChips(row).length" class="muted-chip">No key specs</span>
        </div>
      </template>

      <template v-slot:cell-price="{ row }">
        <span class="price-cell">{{ priceLabel(row) }}</span>
      </template>

      <template v-slot:cell-actions="{ row }">
        <AdminActionMenu>
          <button class="row-btn" @click.stop="popup(row)">Edit</button>
          <button
            class="row-btn danger"
            :disabled="deletingId === row.id"
            @click.stop="deleteItem(row)"
          >{{ deletingId === row.id ? 'Deleting...' : 'Delete' }}</button>
        </AdminActionMenu>
      </template>
    </AdminTable>

    <div v-else-if="allItemsLoaded" class="items-empty-state">
      <div class="items-empty-kicker">No Results</div>
      <p>No {{ typeLabel }} items match this search.</p>
    </div>

    <LoadingOverlay v-else mode="block" min-height="280px" />
  </div>
</template>

<script>
import { mapActions, mapGetters }             from "vuex";
import { EventBus }                           from "../../eventBus";
import LoadingOverlay                         from '../widgets/LoadingOverlay.vue'
import AdminActionMenu                        from './shared/AdminActionMenu.vue'
import AdminTable                             from './shared/AdminTable.vue'
import BulkPartMaintenancePanel               from './shared/BulkPartMaintenancePanel.vue'

export default {
  name: 'Items',
  components:{
    AdminActionMenu,
    AdminTable,
    BulkPartMaintenancePanel,
    LoadingOverlay
  },
  computed: {
    ...mapGetters([
      'getFetchedItems',
      'allItemsLoaded'
    ]),
    filteredItems: function(){
      var items = this.getFetchedItems
      if(!items || items.length == 0){
        return []
      }

      items = items.slice()

      if(items[0] && items[0].id < 20){
        items = items.reverse()  
      }
      
      this.setItems(this.getFilteredList(items, this.activeFiltersV2))
      return  this.getFilteredList(items, this.activeFiltersV2)
    },
    typeLabel: function() {
      return this.formatPartType(this.$route.query.type || this.type || 'parts')
    },
    selectedParts: function() {
      const selected = this.selectedPartKeys
      return (this.getFetchedItems || [])
        .filter((item) => selected.indexOf(this.selectionKey(item)) >= 0)
        .map((item) => Object.assign({}, item, { type: item.type || this.type }))
    },
    allVisibleSelected: function() {
      return this.filteredItems.length > 0 && this.filteredItems.every((item) => this.isSelected(item))
    },
    someVisibleSelected: function() {
      const selectedCount = this.filteredItems.filter((item) => this.isSelected(item)).length
      return selectedCount > 0 && selectedCount < this.filteredItems.length
    },
    itemColumns: function() {
      return [
        { key: 'select', label: '', width: '46px', nowrap: true },
        { key: 'image', label: '', width: '86px', nowrap: true },
        { key: 'part', label: 'Part', width: '30%', breakable: true, sortable: true, defaultDir: 'asc', sortAccessor: (r) => this.itemName(r) },
        { key: 'asin', label: 'Catalog ID', width: '130px', nowrap: true, sortable: true, defaultDir: 'asc', sortAccessor: (r) => Number(r.id) || 0 },
        { key: 'specs', label: 'Key Specs', width: '34%', breakable: true },
        { key: 'price', label: 'Price', width: '116px', align: 'right', nowrap: true, sortable: true, sortAccessor: (r) => Number(this.pickValue(r, ['price.price', 'amzn_price'])) || 0 },
        { key: 'actions', label: '', width: '150px', align: 'right', nowrap: true }
      ]
    },
  },
  data() {
    return {
      search: '',
      type: '',
      items: 'empty',
      categoryChosen: false,
      currentCategory: '',
      category: '',
      activeFiltersV2: {},
      deletingId: '',
      showArchived: false,
      selectedPartKeys: [],
      bulkMaintenanceBusy: false
    }
  },
  watch: {
    '$route.query.search': function () {
      this.syncSearchFromRoute()
    },
    '$route.query.type': function () {
      this.clearSelection()
      this.type = this.getType()
      this.setCurrentPartType(this.type)
      this.syncSearchFromRoute()
      this.syncArchivedFromRoute()
    },
    '$route.query.showArchived': function () {
      this.syncArchivedFromRoute()
    }
  },
  methods: {
    ...mapActions([
      'setPartAttribs',
      'setCurrentPartType',
      //'setItems',
      'setItem',
      'deletePart',
      'fetchItems'
    ]),
    setItems(){
      this.items = this.getFetchedItems
    },
    getFilteredList(items, attribs) {
            var result = []
            if(items && items !== null && typeof(attribs) == 'object' && Object.keys(attribs).length >0){
                console.log('filtering ', items, ' with attribs ', attribs)
                items.map(item => {
                if(attribs[item.type]){
                    Object.keys(attribs[item.type]).map(option => {
                    console.log(option)
                    if(option == 'RAM_Type' && item.type == 'cpu'){
                        option = 'ram'
                    }
                    if(item.compat[option]){
                        console.log(item.compat[option])
                    }
                    if(item.specs[option]){
                        console.log(item.specs[option])
                    }
                    console.log(option)
                    })
                }
                })
            }
            if(items && items !== null && items.length >0){
                if (this.search && this.search !== "") {
                    var query = this.search.toLowerCase()
                    items.map((item) => {
                        var model = item.model ? item.model.toLowerCase() : ''
                        var asin = item.asin ? item.asin.toLowerCase() : ''
                        if(model.includes(query)
                            || asin.includes(query)
                            || JSON.stringify(item).toLowerCase().includes(query)){
                                result.push(item)
                            }
                    });
                } 
                if(result.length >0){
                    if(this.getCompatibleMode == true){
                        return result.filter((item) => {
                            return this.checkPartCompat({compatTable: this.getCompatLib, type: item.type, asin: item.asin}) !==false
                        })
                    }
                    else {
                        return result
                    }
                }
                if(result.length == 0){
                    if(this.search && this.search.trim() !== ''){
                        return []
                    }
                    if(this.getCompatibleMode == true){
                        return items.filter((item) => {
                            return this.checkPartCompat({compatTable: this.getCompatLib, type: item.type, asin: item.asin}) !==false
                        })
                    }
                    else {
                        return items
                    }
                }
            }
            else return this.getFetchedItems;
        },
    popup(item){
      this.setItem(item)
      this.$router.push({
        path: '/users/items-controller/editpart',
        query: {
          type: item.type,
          id: item.id
        }
      }).catch(()=>{})
    },
    selectionKey(item){
      if(!item || !item.id){
        return ''
      }
      return this.normalizeCatalogType(item.type || this.type) + ':' + item.id
    },
    normalizeCatalogType(type){
      const normalized = String(type || '').toLowerCase()
      if(normalized === 'graphics'){
        return 'gpu'
      }
      if(normalized === 'processor'){
        return 'cpu'
      }
      return normalized
    },
    isSelected(item){
      return this.selectedPartKeys.indexOf(this.selectionKey(item)) >= 0
    },
    togglePartSelection(item){
      if(this.bulkMaintenanceBusy){
        return
      }
      const key = this.selectionKey(item)
      if(!key){
        return
      }
      if(this.selectedPartKeys.indexOf(key) >= 0){
        this.selectedPartKeys = this.selectedPartKeys.filter((selectedKey) => selectedKey !== key)
      }
      else {
        this.selectedPartKeys = this.selectedPartKeys.concat(key)
      }
    },
    toggleVisibleSelection(){
      if(this.bulkMaintenanceBusy){
        return
      }
      const visibleKeys = this.filteredItems.map((item) => this.selectionKey(item)).filter(Boolean)
      if(this.allVisibleSelected){
        this.selectedPartKeys = this.selectedPartKeys.filter((key) => visibleKeys.indexOf(key) === -1)
      }
      else {
        this.selectedPartKeys = this.selectedPartKeys.concat(visibleKeys)
          .filter((key, index, keys) => keys.indexOf(key) === index)
      }
    },
    clearSelection(){
      this.selectedPartKeys = []
    },
    itemRowClass(item){
      return this.isSelected(item) ? 'is-selected' : ''
    },
    async handleBulkCompleted(event){
      try {
        await this.fetchItems({
          type: this.type,
          includeArchived: this.showArchived
        })
        if(event && typeof event.complete === 'function'){
          event.complete()
        }
      } catch(error){
        if(event && typeof event.complete === 'function'){
          event.complete(error)
        }
      }
    },
    updateArchivedRoute(){
      const query = Object.assign({}, this.$route.query)
      if(this.showArchived){
        query.showArchived = '1'
      }
      else {
        delete query.showArchived
      }
      this.$router.replace({ path: this.$route.path, query: query }).catch(()=>{})
    },
    async deleteItem(item){
      if(!item || !item.id){
        return
      }
      if(!window.confirm('Delete ' + this.itemName(item) + '?')){
        return
      }
      this.deletingId = item.id
      try {
        await this.deletePart({type: item.type, id: item.id, model: item.model})
        await this.fetchItems(item.type)
      } finally {
        this.deletingId = ''
      }
    },
    itemRowKey(item, index){
      if(item && item.id){
        return item.type + '-' + item.id
      }
      if(item && item.asin){
        return item.type + '-' + item.asin
      }
      return index
    },
    itemName(item){
      return this.readableValue(item && (item.model || item.name || item.title)) || 'Unnamed part'
    },
    itemDescription(item){
      const description = this.readableValue(this.pickValue(item, [
        'content.seoSummary',
        'content.originalDescription',
        'description',
        'general.description',
        'general.name',
        'title',
        'brand'
      ]))
      if(description && description !== this.itemName(item)){
        return this.trimText(description, 120)
      }
      const asin = item && item.asin ? 'ASIN ' + item.asin : 'No ASIN'
      return this.formatPartType(item && item.type ? item.type : this.type) + ' · ' + asin
    },
    mainImage(item){
      if(!item){
        return ''
      }
      if(this.readableValue(item.mainImg)){
        return item.mainImg
      }
      if(this.readableValue(item.image)){
        return item.image
      }
      if(this.readableValue(item.img)){
        return item.img
      }
      if(this.readableValue(item.imgUrl)){
        try {
          const parsed = JSON.parse(item.imgUrl)
          if(Array.isArray(parsed)){
            const image = parsed.find((entry) => {
              return this.readableValue(entry) && String(entry).indexOf('<a href') === -1
            })
            return image || ''
          }
        } catch (e) {
          return item.imgUrl
        }
      }
      return ''
    },
    partSpecChips(item){
      const type = (item && item.type ? item.type : this.type || '').toLowerCase()
      const map = {
        cpu: [
          { label: 'Socket', keys: ['compat.socket', 'compat.Socket', 'socket'] },
          { label: 'Base Clock', keys: ['specs.Core_Frequency'] },
          { label: 'Turbo', keys: ['specs.Turbo_Frequency'] },
          { label: 'Cores', keys: ['specs.Number_of_Cores'] },
          { label: 'Threads', keys: ['specs.Number_of_Threads'] }
        ],
        processor: [
          { label: 'Socket', keys: ['compat.socket', 'compat.Socket', 'socket'] },
          { label: 'Base Clock', keys: ['specs.Core_Frequency'] },
          { label: 'Turbo', keys: ['specs.Turbo_Frequency'] },
          { label: 'Cores', keys: ['specs.Number_of_Cores'] },
          { label: 'Threads', keys: ['specs.Number_of_Threads'] }
        ],
        graphics: [
          { label: 'GPU', keys: ['specs.graphics_processor'] },
          { label: 'Memory', keys: ['specs.graphics_ram_capacity'] },
          { label: 'Core Clock', keys: ['specs.GPU_clock_speed'] },
          { label: 'Mem Clock', keys: ['specs.memory_clock_speed'] },
          { label: 'Interface', keys: ['compat.graphics_interface', 'specs.graphics_interface'] }
        ],
        gpu: [
          { label: 'GPU', keys: ['specs.graphics_processor'] },
          { label: 'Memory', keys: ['specs.graphics_ram_capacity'] },
          { label: 'Core Clock', keys: ['specs.GPU_clock_speed'] },
          { label: 'Mem Clock', keys: ['specs.memory_clock_speed'] },
          { label: 'Interface', keys: ['compat.graphics_interface', 'specs.graphics_interface'] }
        ],
        motherboard: [
          { label: 'Socket', keys: ['compat.socket', 'compat.Socket', 'socket'] },
          { label: 'Memory', keys: ['specs.Compatible_Memory', 'compat.ram'] },
          { label: 'Bus Speed', keys: ['specs.Bus_Speed'] },
          { label: 'RAM Speed', keys: ['compat.ram_speed'] },
          { label: 'Size', keys: ['compat.form_factor', 'specs.Size', 'form_factor'] },
          { label: 'CPU Gen', keys: ['compat.processor_generation', 'compat.generation', 'processor_generation'] }
        ],
        ram: [
          { label: 'Type', keys: ['specs.RAM_Type', 'compat.ram_type'] },
          { label: 'Capacity', keys: ['specs.Capacity', 'compat.capacity'] },
          { label: 'Speed', keys: ['specs.RAM_Speed', 'compat.ram_speed'] },
          { label: 'Sticks', keys: ['specs.Number_of_Sticks', 'compat.number_of_items'] }
        ],
        storage: [
          { label: 'Capacity', keys: ['specs.Capacity', 'specs.capacity', 'capacity'] },
          { label: 'Interface', keys: ['specs.hardware_interface', 'compat.compat_interface'] },
          { label: 'Type', keys: ['specs.Disk_Type'] },
          { label: 'Size', keys: ['specs.size', 'compat.size'] },
          { label: 'Transfer', keys: ['specs.data_transfer_rate', 'compat.speed'] }
        ],
        psu: [
          { label: 'Wattage', keys: ['specs.wattage', 'compat.wattage'] },
          { label: 'Efficiency', keys: ['specs.energy_rating'] },
          { label: 'Modular', keys: ['specs.modular'] },
          { label: 'Size', keys: ['compat.form_factor'] }
        ],
        case: [
          { label: 'Size', keys: ['specs.form_factor', 'compat.form_factor'] },
          { label: 'Dimensions', keys: ['specs.dimentions'] },
          { label: 'Weight', keys: ['specs.weight'] },
          { label: 'Expansion', keys: ['specs.expansion_slots'] }
        ]
      }
      const specs = map[type] || map[this.normalizeType(type)] || []
      return specs.map((spec) => {
        return { label: spec.label, value: this.readableValue(this.pickValue(item, spec.keys)) }
      }).filter((chip) => chip.value).slice(0, 5)
    },
    pickValue(item, paths){
      if(!item){
        return ''
      }
      for(let i = 0; i < paths.length; i++){
        const path = paths[i].split('.')
        let current = item
        for(let j = 0; j < path.length; j++){
          if(!current || current[path[j]] === undefined || current[path[j]] === null){
            current = null
            break
          }
          current = current[path[j]]
        }
        if(this.readableValue(current)){
          return current
        }
      }
      return ''
    },
    readableValue(value){
      if(value === undefined || value === null){
        return ''
      }
      const text = String(value).trim()
      const normalized = text.toLowerCase()
      if(!text || normalized === 'n/a' || normalized === 'na' || normalized === 'undefined' || normalized === 'null' || normalized === 'nan'){
        return ''
      }
      return text
    },
    trimText(text, limit){
      if(!text || text.length <= limit){
        return text
      }
      return text.slice(0, limit - 1).trim() + '…'
    },
    priceLabel(item){
      const trackedPrice = this.readableValue(this.pickValue(item, ['price.price']))
      const price = trackedPrice || this.readableValue(item && item.amzn_price)
      if(!price){
        return '—'
      }
      const cleaned = String(price).replace(/^\$/, '')
      const numeric = Number(cleaned)
      if(!isNaN(numeric)){
        const currency = trackedPrice ? this.readableValue(this.pickValue(item, ['price.currency'])) || 'USD' : 'USD'
        try {
          return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency }).format(numeric)
        }
        catch(error){
          return '$' + numeric.toFixed(2)
        }
      }
      return price.indexOf('$') === 0 ? price : '$' + price
    },
    isArchived(item){
      if(!item){
        return false
      }
      const status = String(item.status || item.state || '').toLowerCase()
      if(status.indexOf('archiv') >= 0 || status.indexOf('hidden') >= 0 || status.indexOf('dead') >= 0){
        return true
      }
      if(item.archived === true || item.is_archived === true || item.hidden === true || item.is_hidden === true){
        return true
      }
      return false
    },
    isUnavailable(item){
      if(!item){
        return false
      }
      const status = String(item.availability_status || item.availabilityStatus || '').toLowerCase()
      return item.is_available === false || item.is_available === 0 || item.is_available === '0' ||
        item.available === false || item.unavailable === true || item.is_unavailable === true ||
        status === 'unavailable' || status === 'out_of_stock'
    },
    availabilityLabel(item){
      const status = this.readableValue(item && (item.availability_status || item.availabilityStatus))
      return status ? status.replace(/_/g, ' ') : 'Unavailable'
    },
    normalizeType(type){
      if(type === 'gpu'){
        return 'graphics'
      }
      if(type === 'processor'){
        return 'cpu'
      }
      return type
    },
    getType(){
      this.type = this.$route.query.type 
      return this.type
    },
    syncSearchFromRoute(){
      const routeSearch = this.$route.query.search
      this.search = routeSearch ? String(routeSearch) : ''
    },
    syncArchivedFromRoute(){
      const value = this.$route.query.showArchived
      this.showArchived = value === true || value === '1' || value === 'true' || value === 'yes'
    },
    formatPartType(type) {
      if (!type) {
        return 'parts'
      }

      switch (type.toLowerCase()) {
        case 'cpu':
          return 'CPU'
        case 'gpu':
        case 'graphics':
          return 'graphics'
        case 'ram':
          return 'RAM'
        case 'psu':
          return 'power supply'
        default:
          return type
      }
    },
  },
  mounted(){
    // this.getType();
        // this.setItems(this.items)
        this.getType();
        EventBus.$on('frequency', (frequencyIndex) => {
            this.frequencyGHZ = parseInt(parseFloat(frequencyIndex)*100);
            parseInt(this.frequencyGHZ)
            console.log('frequency emited, frequencyGHZ: '+this.frequencyGHZ)
        });
        EventBus.$on('emitTypeChange', () => {
            this.setItems()
            this.syncSearchFromRoute()
        });
        EventBus.$on('sortApplied', () => {
            this.setItems()
        });
        EventBus.$on('extendListResponse', (bool) => {
            this.listExtended = bool        
        })
  },
  created(){
    this.setCurrentPartType(this.getType())
    this.syncSearchFromRoute()
    this.syncArchivedFromRoute()
    
  },
  beforeDestroy(){
    EventBus.$emit('clearCategory')
  }
}
</script>

<style scoped>
@import '../../assets/css/toolkit-inverse.css';
::-webkit-scrollbar { 
   display:unset !important; 
}

.items-list-page {
	padding-top: 1rem;
}

.items-list-tools {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 0.9rem;
}

.items-searchbar {
	position: relative;
	display: flex;
	align-items: center;
	flex: 1 1 auto;
	margin: 0;
}

.items-searchbar .icon {
	position: absolute;
	left: 0.85rem;
	color: #9198aa;
	font-size: 14px;
	pointer-events: none;
}

.items-search-input {
	width: 100%;
	min-height: 42px;
	padding: 0.6rem 0.85rem 0.6rem 2.45rem;
	border: 1px solid rgba(67, 72, 87, 0.96);
	border-radius: 6px;
	outline: none;
	background: rgba(6, 8, 12, 0.76);
	color: #e5e8ef;
	font-size: 15px;
	letter-spacing: 0;
	transition: border-color 160ms ease, box-shadow 160ms ease, background 160ms ease;
}

.items-search-input::placeholder {
	color: rgba(145, 152, 170, 0.7);
}

.items-search-input:hover {
	border-color: rgba(25, 151, 198, 0.55);
}

.items-search-input:focus {
	border-color: #1997c6;
	background: rgba(8, 10, 15, 0.98);
	box-shadow: 0 0 0 3px rgba(25, 151, 198, 0.11);
}

.items-count {
	flex: 0 0 auto;
	color: #9198aa;
	font-size: 12px;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.06em;
}

.archive-toggle {
	display: inline-flex;
	align-items: center;
	gap: 0.45rem;
	min-height: 34px;
	padding: 0 0.7rem;
	border: 1px solid rgba(67, 72, 87, 0.82);
	border-radius: 6px;
	background: rgba(8, 10, 15, 0.46);
	color: #aab2c2;
	cursor: pointer;
	font-size: 12px;
	font-weight: 800;
	letter-spacing: 0.02em;
	text-transform: uppercase;
	white-space: nowrap;
	transition: border-color 160ms ease, background 160ms ease, color 160ms ease;
}

.archive-toggle:hover {
	border-color: rgba(25, 151, 198, 0.62);
	background: rgba(25, 151, 198, 0.1);
	color: #d7eaf1;
}

.archive-toggle input {
	width: 14px;
	height: 14px;
	margin: 0;
	accent-color: #1997c6;
}

.bulk-select-toggle {
	border-color: rgba(25, 151, 198, 0.5);
}

.part-select {
	display: grid;
	place-items: center;
	width: 28px;
	height: 28px;
	margin: 0;
	border: 1px solid rgba(67, 72, 87, 0.72);
	border-radius: 6px;
	background: rgba(5, 8, 13, 0.5);
	cursor: pointer;
}

.part-select:hover {
	border-color: rgba(25, 151, 198, 0.72);
	background: rgba(25, 151, 198, 0.1);
}

.part-select input {
	width: 15px;
	height: 15px;
	margin: 0;
	accent-color: #1997c6;
}

.items-admin-table >>> .gc-table-row.is-selected .gc-table-cell {
	background: rgba(25, 151, 198, 0.075);
}

.items-admin-table >>> .gc-table-row.is-selected .gc-table-cell:first-child {
	box-shadow: inset 3px 0 0 #1997c6;
}

.items-admin-table {
	min-height: 360px;
}

.part-thumb {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 58px;
	height: 58px;
	border: 1px solid rgba(214, 222, 234, 0.24);
	border-radius: 6px;
	background: #eef2f7;
	box-shadow: inset 0 0 18px rgba(10, 13, 18, 0.18), 0 8px 18px rgba(0, 0, 0, 0.18);
	overflow: hidden;
}

.part-thumb img {
	display: block;
	max-width: 88%;
	max-height: 88%;
	object-fit: contain;
	filter: drop-shadow(0 4px 7px rgba(0, 0, 0, 0.22));
}

.part-thumb.is-empty {
	background: rgba(15, 20, 30, 0.72);
	color: #6f788b;
}

.part-title {
	display: block;
	max-width: 100%;
	padding: 0;
	border: 0;
	background: transparent;
	color: #dce1eb;
	font-size: 15px;
	font-weight: 800;
	line-height: 1.25;
	text-align: left;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	cursor: pointer;
}

.part-title-line {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	min-width: 0;
}

.part-title:hover {
	color: #1fb2e8;
}

.archive-badge {
	flex: 0 0 auto;
	padding: 0.15rem 0.42rem;
	border: 1px solid rgba(224, 192, 126, 0.55);
	border-radius: 999px;
	background: rgba(77, 58, 31, 0.22);
	color: #e3c777;
	font-size: 10px;
	font-weight: 900;
	letter-spacing: 0.04em;
	line-height: 1.1;
	text-transform: uppercase;
}

.archive-badge.is-unavailable {
	border-color: rgba(232, 79, 106, 0.48);
	background: rgba(232, 79, 106, 0.1);
	color: #f3a3b1;
}

.part-subtitle {
	display: block;
	margin-top: 0.25rem;
	color: #8f98aa;
	font-size: 12px;
	line-height: 1.35;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.part-id,
.part-asin {
	display: block;
}

.part-id {
	color: #7f899b;
	font-family: monospace;
	font-size: 11px;
}

.part-asin {
	margin-top: 0.2rem;
	color: #c6ccd8;
	font-size: 12px;
	font-weight: 700;
	overflow: hidden;
	text-overflow: ellipsis;
}

.spec-chip-list {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
}

.spec-chip,
.muted-chip {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	min-height: 25px;
	padding: 0 8px;
	border: 1px solid rgba(84, 91, 109, 0.72);
	border-radius: 6px;
	background: rgba(9, 13, 20, 0.54);
	color: #cdd3df;
	font-size: 11.5px;
	line-height: 1;
}

.spec-chip span {
	color: #858fa2;
	font-size: 10px;
	font-weight: 800;
	text-transform: uppercase;
}

.spec-chip strong {
	color: #dce1eb;
	font-weight: 800;
}

.muted-chip {
	color: #778194;
}

.price-cell {
	color: #d9db24;
	font-size: 15px;
	font-weight: 800;
	white-space: nowrap;
}

.item-actions {
	display: flex;
	justify-content: flex-end;
	gap: 7px;
}

.row-btn {
	height: 29px;
	padding: 0 12px;
	border: 1px solid rgba(67, 72, 87, 0.95);
	border-radius: 6px;
	background: rgba(20, 25, 35, 0.7);
	color: #cfd2da;
	cursor: pointer;
	font-size: 11.5px;
	font-weight: 800;
}

.row-btn:hover:not(:disabled) {
	border-color: rgba(25, 151, 198, 0.75);
	color: #eaf7fc;
}

.row-btn.danger {
	border-color: rgba(230, 71, 89, 0.5);
	background: rgba(230, 71, 89, 0.08);
	color: #ff7e8e;
}

.row-btn.danger:hover:not(:disabled) {
	border-color: rgba(230, 71, 89, 0.82);
	background: rgba(230, 71, 89, 0.16);
	color: #ffd6dc;
}

.row-btn:disabled {
	opacity: 0.5;
	cursor: default;
}

.items-empty-state {
	display: grid;
	place-items: center;
	min-height: 280px;
	padding: 2rem;
	border: 1px solid rgba(67, 72, 87, 0.78);
	border-radius: 6px;
	background: rgba(8, 10, 15, 0.42);
	text-align: center;
}

.items-empty-state {
	display: block;
}

.items-empty-kicker {
	margin-bottom: 0.4rem;
	color: #1997c6;
	font-size: 12px;
	font-weight: 700;
	letter-spacing: 0.8px;
	text-transform: uppercase;
}

.items-empty-state p {
	margin: 0;
	color: #9198aa;
	font-size: 15px;
}

.icon-minus {
  display: none;
}
.icon-minus:hover {
  color: #e64759 !important;
}
.inCompareList {
  display: block !important;
}
.inCompareList-hide-add-btn {
  display: none;
}
.checked {
  color: #1bc98e !important;
}

@media screen and (max-width: 760px) {
	.items-list-tools {
		align-items: stretch;
		flex-wrap: wrap;
	}

	.items-searchbar {
		flex-basis: 100%;
	}

	.archive-toggle,
	.bulk-select-toggle {
		flex: 1 1 auto;
		justify-content: center;
	}
}
</style>
